import { useCallback, useMemo, useState } from 'react';

import { ceilingOf, getNodeState, getRowTag, getViaRoles } from '../../../utils/permissionTree';
import PermissionRow from './PermissionRow';

const NO_TAGS = Object.freeze({});

const initialExpanded = (nodes, expand) => {
  if (expand === 'all') {
    const ids = [];
    const walk = (list) => list.forEach((n) => (n.children?.length ? (ids.push(n.id), walk(n.children)) : null));
    walk(nodes);
    return new Set(ids);
  }
  if (expand === 'top') return new Set(nodes.filter((n) => n.children?.length).map((n) => n.id));
  return new Set();
};

/**
 * Shared permission tree: parent / child rows with a switch, an Own / Groups / Tenant level per
 * row, "Partial · n of m" on parents, and role-derived vs direct rows. The pure logic lives in
 * `utils/permissionTree.js` (build the `draft` there). Used read-only by the member Studios tab
 * and in edit mode by it and by the role builder (CT-5613).
 *
 * @param {object} props
 * @param {object[]} props.nodes Permission tree: `[{ id, label, description?, levels?, children? }]`.
 * @param {Record<string, object>} props.draft Flat map from `buildDraft` / `toggleNode` / `setLevel`.
 * @param {'read'|'edit'} [props.mode] `read`: switches are read-only and inherited rows count as on.
 * @param {Record<string, 'pinned'|'modified'>} [props.tags] Edit-time tags from `diffDraft`.
 * @param {(id: string, on: boolean) => void} [props.onToggle]
 * @param {(id: string, level: string) => void} [props.onLevelChange]
 * @param {'none'|'top'|'all'} [props.expand] Which parents start expanded.
 */
const PermissionTree = ({ nodes, draft, mode = 'read', tags = NO_TAGS, onToggle, onLevelChange, expand = 'none' }) => {
  const [expanded, setExpanded] = useState(() => initialExpanded(nodes, expand));

  const handleExpand = useCallback(
    (id) =>
      setExpanded((current) => {
        const next = new Set(current);
        if (!next.delete(id)) next.add(id);
        return next;
      }),
    [],
  );
  const handleToggle = useCallback((id, on) => onToggle?.(id, on), [onToggle]);
  const handleLevel = useCallback((id, level) => onLevelChange?.(id, level), [onLevelChange]);

  const rows = useMemo(() => {
    const effective = mode === 'read';
    const out = [];
    const walk = (list, depth, parent) =>
      list.forEach((node) => {
        out.push({
          node,
          depth,
          // Primitives, so a row does not re-render because a new ceiling object was built.
          ceilingId: parent && ceilingOf(draft[parent.id]) != null ? parent.id : null,
          ceilingLabel: parent?.label,
          ceilingLevel: parent ? ceilingOf(draft[parent.id]) : null,
          state: getNodeState(node, draft, { effective }),
          badgeState: getNodeState(node, draft, { effective: true }),
          viaRoles: getViaRoles(node, draft, { isEdit: mode === 'edit', aggregate: depth > 0 }),
          tag: getRowTag(node, tags, { aggregate: depth > 0 }),
        });
        if (node.children?.length && expanded.has(node.id)) walk(node.children, depth + 1, node);
      });
    walk(nodes, 0, null);
    return out;
  }, [nodes, draft, mode, expanded, tags]);

  return (
    <div className="rounded-lg border border-edge px-4">
      {rows.map(({ node, depth, ceilingId, ceilingLabel, ceilingLevel, state, badgeState, viaRoles, tag }) => (
        <PermissionRow
          key={node.id}
          node={node}
          entry={draft[node.id]}
          ceilingId={ceilingId}
          ceilingLabel={ceilingLabel}
          ceilingLevel={ceilingLevel}
          state={state}
          badgeState={badgeState}
          viaRoles={viaRoles}
          tag={tag}
          mode={mode}
          depth={depth}
          expanded={expanded.has(node.id)}
          onToggle={handleToggle}
          onLevelChange={handleLevel}
          onExpand={handleExpand}
        />
      ))}
    </div>
  );
};

export default PermissionTree;
